import React from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from 'react-query'
import { CheckCircle, Clock, AlertCircle, Plus } from 'lucide-react'
import { format } from 'date-fns'
import { dashboardService } from '@/services/dashboardService'

const TasksOverview = () => {
  const { data: tasksData, isLoading } = useQuery(
    'dashboard-tasks-overview',
    dashboardService.getTasksOverview,
    {
      select: (response) => response.data,
      refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
    }
  )

  const tasks = tasksData?.data?.tasks || []
  const displayTasks = tasks

  const getStatusIcon = (status) => {
    const icons = {
      todo: Clock,
      in_progress: AlertCircle,
      review: Clock,
      completed: CheckCircle,
      cancelled: AlertCircle
    }
    return icons[status] || Clock
  }

  const getStatusColor = (status) => {
    const colors = {
      todo: 'text-gray-500',
      in_progress: 'text-blue-500',
      review: 'text-yellow-500',
      completed: 'text-green-500',
      cancelled: 'text-red-500'
    }
    return colors[status] || 'text-gray-500'
  }

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'bg-green-100 text-green-800',
      medium: 'bg-yellow-100 text-yellow-800',
      high: 'bg-red-100 text-red-800',
      critical: 'bg-purple-100 text-purple-800'
    }
    return colors[priority] || 'bg-gray-100 text-gray-800'
  }

  const pendingTasks = displayTasks.filter(task => 
    ['todo', 'in_progress', 'review'].includes(task.status)
  )

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow">
        <div className="p-6">
          <div className="animate-pulse">
            <div className="flex items-center justify-between mb-4">
              <div className="h-6 bg-gray-300 rounded w-1/4"></div>
              <div className="h-4 bg-gray-300 rounded w-16"></div>
            </div>
            <div className="space-y-3">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="flex items-start space-x-3 p-3 border border-gray-200 rounded-lg">
                  <div className="h-5 w-5 bg-gray-300 rounded mt-0.5"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-gray-300 rounded w-3/4 mb-2"></div>
                    <div className="flex justify-between">
                      <div className="h-3 bg-gray-300 rounded w-1/4"></div>
                      <div className="h-3 bg-gray-300 rounded w-1/6"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">My Tasks</h3>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-500">
              {pendingTasks.length} pending
            </span>
            <Link
              to="/projects/tasks"
              className="text-sm text-primary-600 hover:text-primary-500"
            >
              View all
            </Link>
          </div>
        </div>
        {/* {JSON.stringify({ tasksData })} */}
        <div className="space-y-3">
          {pendingTasks.slice(0, 4).map((task) => {
            const StatusIcon = getStatusIcon(task.status);
            const statusColor = getStatusColor(task.status);

            return (
              <div
                key={task.id}
                className="flex items-start space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <StatusIcon className={`h-5 w-5 mt-0.5 ${statusColor}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {task.title}
                  </p>
                  <div className="flex items-center justify-between mt-1">
                    <p className="text-xs text-gray-500 truncate">
                      {task.project}
                    </p>
                    <div className="flex items-center space-x-2">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getPriorityColor(
                          task.priority
                        )}`}
                      >
                        {task.priority}
                      </span>
                      <span className="text-xs text-gray-500">
                        {format(new Date(task.due_date), "MMM dd")}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {pendingTasks.length === 0 && (
          <div className="text-center py-8">
            <CheckCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 mb-2">All caught up!</p>
            <p className="text-sm text-gray-400">
              No pending tasks at the moment
            </p>
          </div>
        )}

        <div className="mt-4 pt-4 border-t border-gray-200">
          <Link
            to="/hr/tasks"
            className="flex items-center justify-center w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create new task
          </Link>
        </div>
      </div>
    </div>
  );
}

export default TasksOverview